import React, { FC, useEffect, HTMLAttributes } from "react";
import GradientBorderRadius, {
  GradientStyles,
  HoverStyles,
  RadiusStyles,
} from "./gradient-border-radius";
import clsx from "clsx";

export type InputProps = Omit<HTMLAttributes<HTMLInputElement>, "onChange"> & {
  onChange?: (e: string) => void;
  className?: string;
  gradientClassName?: string;
  placeholder?: string;
  rightSide?: React.ReactNode;
  leftSide?: React.ReactNode;
  value?: string | number | null;
  defaultValue?: string | number;
  type?: "text" | "number";
  readOnly?: boolean;
  min?: number;
  max?: number;
  radius?: keyof typeof RadiusStyles;
  hover?: keyof typeof HoverStyles;
  gradient?: keyof typeof GradientStyles;
  activeWhenInputIsNotEmpty?: boolean;
};

const BaseInput: FC<InputProps> = ({
  onChange = () => {},
  value,
  placeholder = "Type text here",
  rightSide = null,
  leftSide = null,
  defaultValue,
  className,
  gradientClassName,
  type,
  readOnly = false,
  min,
  max,
  radius,
  hover = "r-100",
  gradient = "default-border",
  activeWhenInputIsNotEmpty = true,
}) => {
  const [internalValue, setInternalValue] = React.useState<
    string | number | null | undefined
  >(defaultValue || "");
  const isControlled = value !== undefined;
  const inputValue = isControlled ? value : internalValue;

  useEffect(() => {
    if (!isControlled) {
      setInternalValue(value);
    }
  }, [value]);

  return (
    <GradientBorderRadius
      hover={hover}
      radius={radius}
      gradient={
        activeWhenInputIsNotEmpty && inputValue && inputValue !== ""
          ? "100"
          : gradient
      }
      className={clsx(
        "focus-within:before:bg-gradient-100 w-full h-full",
        gradientClassName
      )}
    >
      <div className="flex justify-center items-center w-full h-full">
        {leftSide}
        <input
          readOnly={readOnly}
          onFocus={(e) => (e.target.placeholder = "")}
          onBlur={(e) => (e.target.placeholder = placeholder)}
          className={clsx(
            "w-full h-full dark:text-white text-black text-xs bg-transparent focus:outline-none  border-[0.1875rem] border-transparent placeholder:text-gray font-normal",
            "pl-[0.2125rem]",
            className
          )}
          value={inputValue ?? ""}
          onChange={({ target: { value } }) => {
            if (max && +value > max) {
              return;
            }

            if (!isControlled) {
              setInternalValue(value);
            }
            onChange(value);
          }}
          placeholder={placeholder}
          type={type}
          min={min}
          max={max}
        />
        {rightSide}
      </div>
    </GradientBorderRadius>
  );
};

const Accordion = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={clsx(
        "w-full h-full flex justify-center items-center text-center",
        className
      )}
    >
      {children}
    </div>
  );
};

const Input = BaseInput as FC<InputProps> & {
  Accordion: typeof Accordion;
};

Input.Accordion = Accordion;

export default Input;
